import React from 'react';
import HtmlVisual from '../digitalTwin/insight/HtmlVisual';
import ReadOnlyChatView, { ReadOnlyChatMessage } from '../digitalTwin/components/ReadOnlyChatView';
import SimulationResultsContent from '../simulationResults/SimulationResultsContent';
import { ItemPayload, simulationItemPath } from './api';

/**
 * How each kind of content is shown to a rater.
 *
 * This registry is the seam that keeps studies code-free: a study picks a `render_kind` its items
 * already have, and nothing here changes. Only a genuinely NEW kind of content costs a component
 * and one line below — which is the bounded change the old design never had, because every new
 * source also meant new SQL, new payload builders and new dispatch in two routers.
 *
 * Every renderer here is the product's own, not a copy: an HTML report goes through the same
 * sandboxed iframe as chat visuals (opaque origin, strict CSP, no network), a chat transcript
 * through the same read-only view as the shared-chat page, a simulation through the results view a
 * customer sees. A rater should judge exactly what the product produces.
 */

export interface ItemRendererProps {
  item: ItemPayload;
  /**
   * The assignment this item is being read under. Holding it is what authorises the read — a rater
   * owns none of the content in a study — so it, not `item_ref`, identifies every fetch.
   */
  assignmentId: number;
  /** The item's position within its unit, which is how the server addresses it. */
  index: number;
  /** True when the unit shows more than one item, so this renderer has half the width. */
  stacked: boolean;
}

export type ItemRenderer = React.FC<ItemRendererProps>;

const HtmlDocument: ItemRenderer = ({ item }) => {
  const html = (item.payload?.html as string) ?? '';
  return <HtmlVisual html={html} />;
};

const ChatTranscript: ItemRenderer = ({ item }) => {
  const messages = (item.payload?.messages as ReadOnlyChatMessage[]) ?? [];
  return <ReadOnlyChatView messages={messages} title={item.title} />;
};

/**
 * The results view fetches its own payload, so it is told exactly where to read from.
 *
 * `isRatingView` on its own means "`simulationId` is an /rate ASSIGNMENT id", and the component
 * builds `/rlhf/assignments/{id}/simulation` from it. A labelling item's `item_ref` is a SIMULATION
 * id, and the two id spaces are unrelated: passing one for the other 404s, or — where the numbers
 * happen to collide — quietly renders somebody else's report under this item's title, which is a
 * rater grading the wrong thing with nothing on screen to reveal it. So the path is passed
 * explicitly, exactly as /rate2's ReportPane does for its two sides.
 */
const Simulation: ItemRenderer = ({ assignmentId, index, stacked }) => (
  <SimulationResultsContent
    key={simulationItemPath(assignmentId, index)}
    simulationId={assignmentId}
    ratingEndpointPath={simulationItemPath(assignmentId, index)}
    isRatingView
    // A unit showing two items gives each half the width; the inputs column is a fixed 380px, so
    // side-by-side would leave the report almost nothing. Same reason ReportPane stacks.
    stackInputs={stacked}
    hideChat
  />
);

const Markdown: ItemRenderer = ({ item }) => (
  <div className="px-6 py-5 text-sm text-gray-800 whitespace-pre-wrap">
    {(item.payload?.text as string) ?? ''}
  </div>
);

/**
 * Shown when an item's kind has no renderer — a study loaded with a render_kind this build does not
 * know. Deliberately explicit rather than blank: a rater must never be asked to score something
 * they cannot see, and a silent empty pane is exactly how that would happen.
 */
const UnknownKind: ItemRenderer = ({ item }) => (
  <div className="px-6 py-10 text-center">
    <p className="text-sm text-gray-700">
      This item is a <code className="font-mono">{item.render_kind}</code>, which this version of
      the app cannot display.
    </p>
    <p className="text-xs text-gray-500 mt-1">
      Please skip it and let us know — scoring it blind would be worse than leaving it.
    </p>
  </div>
);

const RENDERERS: Record<string, ItemRenderer> = {
  html_document: HtmlDocument,
  chat_transcript: ChatTranscript,
  simulation: Simulation,
  markdown: Markdown,
};

export const rendererFor = (renderKind: string): ItemRenderer =>
  RENDERERS[renderKind] ?? UnknownKind;

export const canRender = (renderKind: string): boolean => renderKind in RENDERERS;
